import { Granularity } from 'bindings/time/Granularity';
import * as moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { Asterisk } from '../../../components_v2/dropdown/style/Style';
import { DefaultButton, DefaultSecondaryButton } from '../../../styles/global/css/GlobalButton';
import { Translate, translateToString } from '../../../styles/global/translate';
import importAndReadTableFile, { CustomErrorThrower, MandatoryFieldEnforcer, WrongTypeErrorThrower } from '../../../utils/import';
import { AlertCall, AlertRes } from '../../alert/AlertProvider';
import { creationPopupDropdownStyle } from '../../../components_v2/popup/style/CreationFormStyle';
import { BoolCheckbox } from '../../forms/PopupField';
import { LoadingStateEnum } from '../../import/model';
import { FlexDiv } from '../../products/style';
import { GoalImportBody, importReportGoals } from './action';

type GoalTemplateImport = { id: number | string; date: string } & { [key: string]: number | string };

type FileGoal = {
	id: number,
	goals: Map<string, number>
	date: moment.Moment
}

const LocalButton = styled(DefaultButton)`
	margin: 0;
`;

const LocalSecondaryButton = styled(DefaultSecondaryButton)`
	margin: 0;
`;

const P = styled.p`
	display: flex;
	margin-bottom: 0;
`;

const Td = styled.td`
	text-align: left;
	`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const Tr = styled.tr`
	cursor: pointer;
`;


export function getStartOfGranularity(granularity: Granularity, date: moment.Moment = moment()): moment.Moment {
	const clonedDate = date.clone();
	if (granularity === 'fourMonths') {
		return clonedDate.startOf('month').subtract(clonedDate.month() % 4, 'months');
	}
	return clonedDate.startOf(granularity);
}

export function getNextGranularity(granularity: Granularity, date: moment.Moment = moment(), numberOfIterations: number = 1): moment.Moment {
	const start = getStartOfGranularity(granularity, date);
	if (granularity === 'fourMonths') {
		return start.add(numberOfIterations * 4, 'months');
	}
	return start.add(numberOfIterations * 1, granularity);
}


export default async function importGoals(
	reportId: number,
	alert: AlertCall,
	reportPanels: string[],
	setLoadingState: React.Dispatch<React.SetStateAction<LoadingStateEnum | undefined>>,
	onImportFinished: () => void,
	granularity: Granularity
): Promise<void> {
	let idColumn: number;
	let panelFileLiaisons: Map<string, number>;
	let dateColumn: number | undefined;
	importAndReadTableFile<string, FileGoal, GoalTemplateImport>({
		alert: alert,
		headersFilter: async(headers) => {
			const alertRes = await alert<{ idColumn: number, panelFileLiaisons: Map<string, number>, dateColumn: number | undefined }>({
				title: translateToString('import_'),
				noButtons: true,
				content: (_, onOk) => {
					const [page, setPage] = React.useState(0);
					const [idColumn, setIdColumn] = React.useState(0);
					const [dateColumn, setDateColumn] = React.useState<number>();
					const [panelFileLiaisons, setPanelFileLiaisons] = React.useState<Map<string, number>>(new Map());

					const entries = Array.from(headers.entries());
					let child: React.JSX.Element;
					let buttons: React.JSX.Element;
					if (page == 0) {
						const ddData: DropdownData<number>[] = entries.map(([index, header]) => ({ value: index, label: header }));
						child = <>
							<P><Asterisk /><Translate id='import.goals.p1' /></P>
							<Dropdown
								searchable={false}
								datalist={ddData}
								dropdownStyle={creationPopupDropdownStyle}
								selectedValue={ddData.find(d => d.value === idColumn)}
								onChange={(dd) => setIdColumn(dd.value)}
								name={''}
								required
							/>
							<P><Translate id='import.goals.p4' /></P>
							<Dropdown
								searchable={false}
								datalist={ddData}
								dropdownStyle={creationPopupDropdownStyle}
								selectedValue={ddData.find(d => d.value === dateColumn)}
								onChange={(dd) => setDateColumn(dd.value)}
								name={'dateDD'}
							/>
						</>;
						buttons =
							<LocalButton onClick={() => {
								setPage(1);
							}}><Translate id='next' /></LocalButton>;
					} else {
						buttons = <>
							<LocalSecondaryButton onClick={() => {
								setPage(0);
							}}><Translate id='previous' /></LocalSecondaryButton>
							<LocalButton onClick={() => {
								onOk({ idColumn, panelFileLiaisons, dateColumn });
							}}><Translate id='next' /></LocalButton>
						</>;
						if (reportPanels.length < 2) {
							child = <>
								<P><Translate id='import.goals.p2' /></P>
								<Table style={{ width: '100%' }}>
									<tbody>
										{entries.map(([index, header]) => {
											if (index === idColumn || index === dateColumn) return <></>;
											return (
												<Tr key={index} onClick={() => setPanelFileLiaisons(new Map([['', index]]))}>
													<Td width={25}>
														<BoolCheckbox
															type='radio'
															checked={panelFileLiaisons.get('') === index}
														/>
													</Td>
													<Td>
														<P>{header}</P>
													</Td>
												</Tr>
											);
										})}
									</tbody>
								</Table>
							</>;
						} else {
							const ddData = entries.reduce<DropdownData<number>[]>((acc, [i, header]) => {
								if (i === idColumn || i === dateColumn) return acc;
								acc.push({ value: i, label: header });
								return acc;
							}, []);
							child = <>
								<P><Translate id='import.goals.p3' /></P>
								{
									reportPanels.map((panel, index) => {
										const liaison = panelFileLiaisons.get(panel);
										return <FlexDiv key={index} flow='column' gap='10px' align='start'>
											<P>{panel}</P>
											<Dropdown
												searchable={false}
												datalist={ddData}
												dropdownStyle={creationPopupDropdownStyle}
												selectedValue={ddData.find(d => liaison === d.value)}
												onChange={(dd) => {
													const newLiaisons = new Map(panelFileLiaisons);
													if (dd.value === -1) {
														newLiaisons.delete(panel);
													} else {
														newLiaisons.set(panel, dd.value);
													}
													setPanelFileLiaisons(newLiaisons);
												}}
												name={''}
											/>
										</FlexDiv>;
									})
								}
							</>;
						}
					}


					return (
						<FlexDiv flow='column' width='100%' align='start' gap='50px'>
							<FlexDiv flow='column' gap='10px' align='start'>
								{child}
							</FlexDiv>
							<FlexDiv gap='10px'>
								{buttons}
							</FlexDiv>
						</FlexDiv>
					);
				},
			});
			if (alertRes.res == AlertRes.Break) return new Map();
			if (!alertRes.data || alertRes.data.panelFileLiaisons.size === 0) {
				throw 'No columns selected for data';
			}
			idColumn = alertRes.data.idColumn;
			panelFileLiaisons = alertRes.data.panelFileLiaisons;
			dateColumn = alertRes.data.dateColumn;
			const fileSet = new Set<number>(panelFileLiaisons.values());
			const newHeaders = new Map<number, string>();
			for (const [index, header] of headers.entries()) {
				if (index === idColumn || index === dateColumn || fileSet.has(index)) {
					newHeaders.set(index, header);
				}
			}
			return newHeaders;
		},
		switchHeader: (row, value: unknown, header: string, headerIndex: number, customErrorThrower: CustomErrorThrower, wrongTypeErrorThrower: WrongTypeErrorThrower) => {
			if (headerIndex === idColumn) {
				if (typeof value !== 'number') {
					throw wrongTypeErrorThrower(header, value, 'number');
				}
				if (value < 0) {
					throw customErrorThrower(`Goal ID cannot be negative, got ${value}`);
				}
				row.id = parseInt(value.toFixed(0));
			} else if (headerIndex === dateColumn) {
				if (typeof value !== 'string' && !moment.isDate(value)) {
					throw wrongTypeErrorThrower(header, value, 'string');
				}
				row.date = getStartOfGranularity(granularity, moment(value));
			} else {
				for (const [panel, index] of panelFileLiaisons.entries()) {
					if (headerIndex === index) {
						if (typeof value !== 'number') {
							throw wrongTypeErrorThrower(header, value, 'number');
						}
						if (value < 0) {
							throw customErrorThrower(`Goal value cannot be negative, got ${value}`);
						}
						row.goals ??= new Map();
						row.goals.set(panel, value);
					}
				}
			}
			return row;
		},
		unPartialType: (row, mandatoryFieldEnforcer: MandatoryFieldEnforcer) => {
			if (!row.goals || row.goals.size === 0) {
				return undefined;
			}
			mandatoryFieldEnforcer(row.id, 'id');
			row.date ??= getStartOfGranularity(granularity, moment());
			return row as FileGoal;
		},
		onDataRead: (data) => {
			const panelSet = new Set<string>();
			for (const row of data) {
				for (const [goal] of row.goals) {
					panelSet.add(goal);
				}
			}
			const panels = Array.from(panelSet);
			const body = data.reduce<GoalImportBody>((acc, row) => {
				acc.goals[row.id] ??= [];
				let panelIndex = 0;
				for (const panel of panels) {
					const value = row.goals.get(panel);
					if (value === undefined) {
						if (acc.goals[row.id][panelIndex] === undefined) {
							acc.goals[row.id][panelIndex] = undefined;
						}
						++panelIndex;
						continue;
					}
					acc.goals[row.id][panelIndex] ??= [];
					acc.goals[row.id][panelIndex]!.push({ value, date: row.date.format('YYYY-MM-DD'), dashboard: true });
					++panelIndex;
				}

				return acc;
			}, { panels, goals: {} });
			if (Object.entries(body.goals).length == 0) {
				return;
			}
			setLoadingState(LoadingStateEnum.LOADING);
			importReportGoals(reportId, body)
				.catch(() => setLoadingState(LoadingStateEnum.ERROR))
				.then(onImportFinished);
		},
		headerFallback: header => header?.toString(),
		genTemplate: () => {
			function genRow() {
				const now = getStartOfGranularity(granularity);
				const res: GoalTemplateImport[] = [];
				const baseRow: GoalTemplateImport = {
					id: Math.floor(Math.random() * 100000),
					date: now.format('YYYY-MM-DD'),
				};
				for (const panel of reportPanels) {
					baseRow[panel === '' ? 'data' : panel] = Math.floor(Math.random() * 100);
				}
				res.push(baseRow);
				for (let i = 0; i < 3; i++) {
					const row = {
						id: baseRow.id,
						date: getNextGranularity(granularity, now, i + 1).format('YYYY-MM-DD')
					};

					for (const panel of reportPanels) {
						row[panel === '' ? 'data' : panel] = Math.floor(Math.random() * 100);
					}
					res.push(row);
				}
				return res;
			}

			return [1, 2, 3].map(genRow).flat();
		},
	});
}
